<template>
  <div class="tu-prq-modal" @mousedown.self="$emit('close')">
    <div class="tu-prq-card" role="dialog" aria-modal="true" :aria-labelledby="titleId">
      <button type="button" class="tu-prq-x" aria-label="Close" @click="$emit('close')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
      </button>
      <div class="tu-prq-head">
        <span class="tu-prq-ico" aria-hidden="true"><tu-ui-icon :name="icon" :size="22" /></span>
        <div>
          <span class="tu-prq-kicker">{{ kicker }}</span>
          <h2 :id="titleId" class="tu-prq-title">{{ title }}</h2>
        </div>
      </div>
      <span v-if="row" class="tu-prq-level" :class="`tu-prq-level--${row.level}`">{{ row.level_label }}</span>
      <p v-if="about" class="tu-prq-about">{{ about }}</p>

      <template v-if="item">
        <div class="tu-prq-stats">
          <div class="tu-prq-stat">
            <strong>{{ askPct }}%</strong>
            <span>{{ item.count.toLocaleString('en-US') }} of {{ total.toLocaleString('en-US') }} {{ programsNoun }} ask for it</span>
          </div>
          <div v-if="item.required" class="tu-prq-stat">
            <strong>{{ item.required.toLocaleString('en-US') }}</strong>
            <span>make it required</span>
          </div>
        </div>
        <ul v-if="details.length" class="tu-prq-details">
          <li v-for="d in details" :key="d.label">
            <span class="tu-prq-details__ico" aria-hidden="true"><tu-ui-icon :name="d.icon" :size="18" /></span>
            <span><small>{{ d.label }}</small><b>{{ d.value }}</b></span>
          </li>
        </ul>
        <div v-if="examCredit.length" class="tu-prq-cbe">
          <h3 class="tu-prq-cbe__title">Credit by exam</h3>
          <div v-for="c in examCredit" :key="c.key" class="tu-prq-cbe__row">
            <span class="tu-prq-cbe__name">{{ c.label }}</span>
            <span class="tu-prq-cbe__bar" aria-hidden="true"><i :style="{ width: `${c.pct}%` }" /></span>
            <span class="tu-prq-cbe__val">{{ c.yes }} of {{ c.n }} accept</span>
          </div>
          <p v-if="showsExamCredit" class="tu-prq-exam-note">Rules vary. Confirm CLEP or DSST credit with the school before you test.</p>
        </div>
      </template>

      <dl v-else-if="facts.length" class="tu-prq-facts">
        <div v-for="f in facts" :key="f.label"><dt>{{ f.label }}</dt><dd>{{ f.value }}</dd></div>
      </dl>

      <p v-if="row && row.notes" class="tu-prq-notes">{{ row.notes }}</p>

      <div v-if="item && item.examples && item.examples.length" class="tu-prq-examples">
        <span>How schools list it</span>
        <ul><li v-for="e in item.examples" :key="e">{{ e }}</li></ul>
      </div>

      <p v-if="row && showsExamCredit" class="tu-prq-exam-note">Credit by exam rules vary. Confirm CLEP or DSST credit with the school before you test.</p>

      <p class="tu-prq-foot">
        <template v-if="row">
          <a v-if="row.source_url" :href="row.source_url" target="_blank" rel="noopener noreferrer">From the school's site</a>
          <span v-if="verified">{{ row.source_url ? ' · ' : '' }}Last verified {{ verified }}.</span>
          Requirements change each cycle, so confirm with the school before you apply.
        </template>
        <template v-else>Each program sets its own list. Open a program on this path to see exactly what it asks for.</template>
      </p>
      <router-link v-if="item && actionLabel && actionHref" :to="actionHref" target="_blank" rel="noopener" class="tu-prq-action" @click.native="$emit('action')">{{ actionLabel }}<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg></router-link>
      <button v-else-if="item && actionLabel" type="button" class="tu-prq-action" @click="$emit('action')">{{ actionLabel }}</button>
    </div>
  </div>
</template>

<script>
import TuUiIcon from '@/tu/components/UiIcon.vue'

export const PREREQ_GROUP_ICONS = {
  college_course: 'book_open', entrance_exam: 'edit_note', certification: 'medal',
  compliance: 'verify', general: 'profile', experience: 'users_three', prep_course: 'book_open'
}

const yesNo = v => (v === true ? 'Yes' : v === false ? 'No' : null)

/**
 * One prerequisite, opened from a list. Pass `item` (a summary across many programs, from
 * public-summary) or `row` (one school's requirement, from public). Emits close and action.
 */
export default {
  name: 'TuPrereqModal',
  components: { TuUiIcon },
  props: {
    item: { type: Object, default: null },
    row: { type: Object, default: null },
    group: { type: String, default: '' },
    programLabel: { type: String, default: '' },
    total: { type: Number, default: 0 },
    actionLabel: { type: String, default: '' },
    actionHref: { type: String, default: '' }
  },
  computed: {
    titleId() { return `tu-prq-${(this.item && this.item.key) || (this.row && this.row.id) || 'x'}` },
    type() { return this.group || (this.row && this.row.type) || '' },
    icon() { return PREREQ_GROUP_ICONS[this.type] || 'verify' },
    title() { return this.item ? this.item.label : (this.row && this.row.name) || '' },
    kicker() {
      if (this.row) return this.row.type_label || 'Prerequisite'
      return this.programLabel ? `${this.programLabel} prerequisite` : 'Prerequisite'
    },
    about() { return this.item ? this.item.about : (this.row && this.row.item_about) || '' },
    showsExamCredit() {
      if (this.item) return this.examCredit.some(c => c.key !== 'online_lab')
      return this.facts.some(f => /^(CLEP|ACE|DSST) credit$/.test(f.label))
    },
    programsNoun() { return this.programLabel ? `${this.programLabel} programs` : 'programs' },
    askPct() { return Math.max(1, Math.round(((this.item && this.item.share) || 0) * 100)) },
    /** Summary facts as labelled tiles: grade, recency, lab, typical score, due. */
    details() {
      const i = this.item; if (!i) return []
      const out = []
      if (this.type === 'college_course') {
        if (i.common_grade) out.push({ icon: 'medal', label: 'Minimum grade', value: `${i.common_grade} or better` })
        if (i.median_recency_years) out.push({ icon: 'duration', label: 'Complete within', value: `${i.median_recency_years} years` })
        if (i.lab_share != null && i.lab_share >= 0.5) out.push({ icon: 'flask', label: 'Lab', value: 'Usually required' })
      }
      if (this.type === 'entrance_exam' && i.median_score != null) out.push({ icon: 'edit_note', label: 'Typical minimum score', value: String(i.median_score) })
      if (i.common_due_by) out.push({ icon: 'deadline', label: 'Usually due', value: i.common_due_by })
      return out
    },
    /** CLEP, DSST, ACE and online lab, where at least 3 programs said either way. */
    examCredit() {
      const cbe = (this.item && this.type === 'college_course' && this.item.credit_by_exam) || {}
      return [['clep', 'CLEP'], ['dsst', 'DSST'], ['ace', 'ACE'], ['online_lab', 'Online lab']]
        .map(([key, label]) => { const c = cbe[key]; return c && c.yes + c.no >= 3 ? { key, label, yes: c.yes, n: c.yes + c.no, pct: Math.round((c.yes / (c.yes + c.no)) * 100) } : null })
        .filter(Boolean)
    },
    verified() {
      const d = this.row && this.row.verified_at
      if (!d) return ''
      const t = new Date(d)
      return isNaN(t) ? '' : t.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    facts() {
      return this.item ? this.summaryFacts() : this.row ? this.rowFacts() : []
    }
  },
  methods: {
    summaryFacts() {
      const i = this.item; const out = []
      const prog = this.programLabel ? `${this.programLabel} programs` : 'programs'
      out.push({ label: 'Asked for by', value: `${i.count.toLocaleString('en-US')} of ${this.total.toLocaleString('en-US')} ${prog} (${Math.round(i.share * 100)}%)` })
      if (i.required) out.push({ label: 'Required at', value: `${i.required.toLocaleString('en-US')} of them` })
      if (this.type === 'college_course') {
        if (i.common_grade) out.push({ label: 'Usual minimum grade', value: `${i.common_grade} or better` })
        if (i.median_recency_years) out.push({ label: 'Usually completed within', value: `${i.median_recency_years} years` })
        if (i.lab_share != null && i.lab_share >= 0.5) out.push({ label: 'Lab', value: 'Usually with a lab' })
        const cbe = i.credit_by_exam || {}
        const said = (k, name) => { const c = cbe[k]; if (c && c.yes + c.no >= 3) out.push({ label: name, value: `Accepted at ${c.yes} of ${c.yes + c.no} programs that say` }) }
        said('clep', 'CLEP credit'); said('ace', 'ACE credit'); said('dsst', 'DSST credit'); said('online_lab', 'Online lab')
      }
      if (this.type === 'entrance_exam' && i.median_score != null) out.push({ label: 'Typical minimum score', value: `${i.median_score}, varies by program` })
      if (i.common_due_by) out.push({ label: 'Usually due', value: i.common_due_by })
      return out
    },
    rowFacts() {
      const r = this.row; const out = []
      if (r.min_grade) out.push({ label: 'Minimum grade', value: `${r.min_grade} or better` })
      if (r.min_score != null) out.push({ label: 'Minimum score', value: `${r.min_score}${r.score_label ? ` (${r.score_label})` : ''}` })
      if (r.points != null) out.push({ label: 'Admission points', value: String(r.points) })
      if (r.credits) out.push({ label: 'Credits', value: `${r.credits}${r.has_lab ? ' with lab' : ''}` })
      else if (r.has_lab) out.push({ label: 'Lab', value: 'Lab required' })
      if (r.recency_years) out.push({ label: 'Completed within', value: `${r.recency_years} years` })
      if (r.due_by_label) out.push({ label: 'Due', value: r.due_by_label })
      if (r.choice_group) out.push({ label: 'One of', value: r.choice_group })
      if (r.certification_program_title) out.push({ label: 'Credential', value: r.certification_program_title })
      const acc = [['CLEP credit', r.accepts_clep], ['ACE credit', r.accepts_ace], ['DSST credit', r.accepts_dsst], ['Online lab', r.accepts_online_lab]]
      acc.forEach(([label, v]) => { const t = yesNo(v); if (t) out.push({ label, value: t === 'Yes' ? 'Accepted' : 'Not accepted' }) })
      return out
    }
  }
}
</script>

<style scoped>
.tu-prq-modal { position: fixed; inset: 0; z-index: 2000; display: grid; place-items: center; padding: 16px; background: rgba(15, 18, 24, .5); }
.tu-prq-card { position: relative; width: min(560px, 100%); max-height: calc(100vh - 32px); overflow-y: auto; padding: 28px 28px 24px; border-radius: 18px; background: var(--tu-surface, #fff); box-shadow: 0 24px 60px rgba(15, 18, 24, .25); box-sizing: border-box; }
.tu-prq-x { position: absolute; top: 14px; right: 14px; display: grid; place-items: center; width: 36px; height: 36px; border: 0; border-radius: 50%; background: var(--tu-surface-2, #f3f5f8); color: var(--tu-ink); cursor: pointer; }
.tu-prq-x svg { width: 18px; height: 18px; }
.tu-prq-head { display: flex; align-items: center; gap: 14px; padding-right: 40px; }
.tu-prq-ico { display: grid; place-items: center; flex: none; width: 48px; height: 48px; border-radius: 14px; background: var(--tu-blue-tint); color: var(--tu-blue-deep); }
.tu-prq-ico .tu-ui-icon { display: inline-flex; }
.tu-prq-kicker { display: block; font-size: 12px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; color: var(--tu-blue); }
.tu-prq-title { margin: 4px 0 0; font-family: var(--tu-font-display); font-size: 24px; line-height: 1.2; letter-spacing: -.015em; color: var(--tu-ink); }
.tu-prq-level { display: inline-block; margin-top: 14px; padding: 4px 10px; border-radius: 999px; background: var(--tu-surface-2, #f3f5f8); font-size: 12.5px; font-weight: 700; color: var(--tu-ink-soft); }
.tu-prq-level--required { background: var(--tu-blue-tint); color: var(--tu-blue-deep); }
.tu-prq-about { margin: 14px 0 0; font-size: 15px; line-height: 1.6; color: var(--tu-ink-soft); }
.tu-prq-facts { margin: 18px 0 0; padding: 4px 16px; border-radius: 12px; background: var(--tu-surface-2, #f3f5f8); }
.tu-prq-facts > div { display: flex; justify-content: space-between; gap: 16px; padding: 11px 0; }
.tu-prq-facts > div + div { border-top: 1px solid var(--tu-line-soft); }
.tu-prq-facts dt { margin: 0; font-size: 13.5px; color: var(--tu-ink-soft); }
.tu-prq-facts dd { margin: 0; font-size: 14px; font-weight: 700; color: var(--tu-ink); text-align: right; }
.tu-prq-notes { margin: 14px 0 0; font-size: 14.5px; line-height: 1.6; color: var(--tu-ink); }
.tu-prq-examples { margin-top: 18px; }
.tu-prq-examples > span { font-size: 12.5px; font-weight: 600; color: var(--tu-ink-mute); }
.tu-prq-examples ul { list-style: none; display: flex; flex-wrap: wrap; gap: 6px; margin: 8px 0 0; padding: 0; }
.tu-prq-examples li { padding: 5px 10px; border-radius: 8px; border: 1px solid var(--tu-line); font-size: 13px; color: var(--tu-ink-soft); }
.tu-prq-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(0, 1fr)); gap: 10px; margin-top: 18px; }
.tu-prq-stat { padding: 16px; border-radius: 14px; background: var(--tu-blue); color: #fff; }
.tu-prq-stat + .tu-prq-stat { background: #181818; }
.tu-prq-stat strong { display: block; font-family: var(--tu-font-display); font-size: 30px; line-height: 1.05; letter-spacing: -.02em; }
.tu-prq-stat span { display: block; margin-top: 6px; font-size: 13px; line-height: 1.4; color: rgba(255,255,255,.82); }
.tu-prq-details { list-style: none; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin: 10px 0 0; padding: 0; }
.tu-prq-details li { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border: 1px solid var(--tu-line); border-radius: 14px; }
.tu-prq-details__ico { display: grid; place-items: center; flex: none; width: 36px; height: 36px; border-radius: 10px; background: var(--tu-blue-tint); color: var(--tu-blue-deep); }
.tu-prq-details__ico .tu-ui-icon { display: inline-flex; }
.tu-prq-details small { display: block; font-size: 12px; color: var(--tu-ink-mute); }
.tu-prq-details b { display: block; font-size: 14.5px; font-weight: 700; color: var(--tu-ink); }
.tu-prq-cbe { margin-top: 18px; padding: 16px; border-radius: 14px; background: var(--tu-surface-2, #f3f5f8); }
.tu-prq-cbe__title { margin: 0 0 10px; font-family: var(--tu-font-text); font-size: 13px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--tu-ink-soft); }
.tu-prq-cbe__row { display: grid; grid-template-columns: 82px minmax(0, 1fr) auto; align-items: center; gap: 12px; padding: 6px 0; }
.tu-prq-cbe__name { font-size: 14px; font-weight: 700; color: var(--tu-ink); }
.tu-prq-cbe__bar { height: 8px; border-radius: 999px; background: var(--tu-line); overflow: hidden; }
.tu-prq-cbe__bar i { display: block; height: 100%; border-radius: 999px; background: var(--tu-blue); }
.tu-prq-cbe__val { font-size: 13px; font-weight: 600; color: var(--tu-ink-soft); white-space: nowrap; }
.tu-prq-cbe .tu-prq-exam-note { margin-top: 10px; }
.tu-prq-action svg { width: 18px; height: 18px; margin-left: 6px; }
a.tu-prq-action { text-decoration: none !important; color: var(--tu-on-cta) !important; }
@media (max-width: 540px) { .tu-prq-details { grid-template-columns: 1fr; } .tu-prq-stat strong { font-size: 26px; } }
.tu-prq-exam-note { margin: 14px 0 0; font-size: 13.5px; line-height: 1.55; color: var(--tu-ink-soft); }
.tu-prq-foot { margin: 18px 0 0; font-size: 13px; line-height: 1.55; color: var(--tu-ink-mute); }
.tu-prq-foot a { font-weight: 600; color: var(--tu-blue); }
.tu-prq-action { display: flex; align-items: center; justify-content: center; width: 100%; min-height: 46px; margin-top: 18px; border: 0; border-radius: var(--tu-r-sm, 10px); background: var(--tu-cta); color: var(--tu-on-cta); font: 700 15px var(--tu-font-text); cursor: pointer; }
.tu-prq-action:hover { background: var(--tu-cta-deep); }
@media (max-width: 540px) { .tu-prq-card { padding: 22px 18px 18px; } .tu-prq-title { font-size: 21px; } }
</style>
